import type { Coding, Patient, Reference } from '@medplum/fhirtypes'
import { ExtendedObservation } from '@seen/core/models/observation'
import {
  buildDerivedObservation,
  computedDerivationKey,
  derivedObservationFields,
  derivedObservationQuery,
  recordedDerivationKey,
} from '@seen/core/observation-logic/derivations/record'
import {
  derivedValueInUnit,
  type DerivationRule,
  missingRequirements,
  rulesForTarget,
} from '@seen/core/observation-logic/derivations/rules'
import {
  interpretScore,
  type ScoreBand,
} from '@seen/core/observation-logic/derivations/scores'
import { STARTER_DERIVATIONS } from '@seen/core/observation-logic/derivations/starter'
import { bandSetFor } from '@seen/core/observation-logic/interpretation'
import {
  type ObservationValue,
  observationValueOf,
} from '@seen/core/observation-logic/value'
import type { ResourceBindingId } from '@seen/core/resourceBindingRegistry'
import type { BandSet } from '@seen/core/terminology/interpretations'
import { generateUUID } from '@seen/core/utils'
import { useQueryClient } from '@tanstack/react-query'
import type { Editor } from '@tiptap/core'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { toast } from 'sonner'
import { derivedRuleFor } from '#src/components/rich-text-editor/ObservationNode/derived/derivedNode.ts'
import {
  type DerivationSource,
  useDerivationInputs,
} from '#src/components/rich-text-editor/ObservationNode/derived/useDerivationInputs.ts'
import type { ObservationNodeAttributes } from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useNodeRecord } from '#src/components/rich-text-editor/ObservationNode/useNodeRecord.ts'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useDebouncedCallback } from '#src/hooks/useDebouncedCallback.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'

const DERIVED_SAVE_DEBOUNCE_MS = 500

export interface DerivedObservationState {
  rule: DerivationRule | undefined
  alternatives: DerivationRule[]
  // What the rule computes now; null while an input or requirement is
  // missing.
  computed: ObservationValue | null
  recorded: ExtendedObservation | undefined
  // What the node shows: the recorded value when changed by hand, else the
  // computed one, else what was recorded.
  shown: ObservationValue | null
  band: ScoreBand | undefined
  // The published cutoffs the value is banded by, when the code has any.
  bandSet: BandSet | undefined
  sources: DerivationSource[]
  // The participant values the rule reads (sex, age), as resolved here.
  requirementValues: {
    binding: ResourceBindingId
    value: ObservationValue | null
  }[]
  missingInputs: Coding[]
  // Missing inputs with no node in the note to fill in, which Add inserts.
  absentFromNote: Coding[]
  // The registry bindings the rule needs that resolve to nothing usable.
  missingRequirements: ResourceBindingId[]
  override: boolean
  // Sets the value by hand, which stops recalculation.
  setManualValue: (value: number | undefined) => void
  // Goes back to the computed value.
  recalculate: () => void
}

// A computed node's value, kept on its own Observation: created once per
// node (by its id, so two editors cannot both create it), patched when an
// input changes, cleared when an input is removed, and left alone once
// changed by hand until recalculated. Where the note holds its answers until
// signed, the value is the node's draft instead, with the same behaviour.
export function useDerivedObservation({
  editor,
  getPos,
  attributes,
  updateAttributes,
  participant,
  recordStatus = 'final',
  deferred = false,
  editable,
  rules = STARTER_DERIVATIONS,
}: {
  editor: Editor
  getPos: () => number | undefined
  attributes: ObservationNodeAttributes
  updateAttributes: (attributes: Partial<ObservationNodeAttributes>) => void
  participant: Reference<Patient> | undefined
  // The status of a record written now; undefined where the answer is held
  // in the note and written when it is signed.
  recordStatus?: 'preliminary' | 'final'
  deferred?: boolean
  editable: boolean
  rules?: readonly DerivationRule[]
}): DerivedObservationState {
  const medplum = useMedplum()
  const queryClient = useQueryClient()
  const practitioner = useCurrentPractitioner()
  const { mutateAsync: patch } = usePatchMutation<ExtendedObservation>()
  const target: Coding = useMemo(
    () => ({
      system: attributes.system,
      code: attributes.code,
      display: attributes.codingDisplay,
    }),
    [attributes.system, attributes.code, attributes.codingDisplay]
  )
  const rule = derivedRuleFor(attributes, rules)
  const alternatives = rulesForTarget(target, rules)
  const inputs = useDerivationInputs({ editor, getPos, rule, participant })
  const node = useNodeRecord()
  const { data: read } = useRead(
    ExtendedObservation,
    node.isDraft ? undefined : attributes.reference
  )
  const recorded = node.isDraft ? node.observation : read
  const unmetRequirements = missingRequirements(rule, inputs.inputs)
  const override = attributes.derivedOverride === true
  const computed =
    rule && inputs.missing.length === 0 && unmetRequirements.length === 0
      ? derivedValueInUnit(rule, rule.compute(inputs.inputs), attributes.unit)
      : null
  const recordedValue = recorded ? observationValueOf(recorded) : null
  const shown = override ? recordedValue : (computed ?? recordedValue)
  const bandSetId = attributes.derivedBandsId ?? null
  const bandSet = bandSetFor(target, bandSetId)
  const band =
    shown?.kind === 'number' && bandSet
      ? interpretScore(shown.value, bandSet.bands)
      : undefined
  const requirementValues = (rule?.requires ?? []).map(({ binding }) => ({
    binding,
    value: inputs.inputs.variable(binding),
  }))

  // The node needs an id to name its record by. Nodes inserted before ids
  // existed get one here.
  useEffect(() => {
    if (editable && rule && !attributes.id) {
      updateAttributes({ id: generateUUID() })
    }
  }, [editable, rule, attributes.id, updateAttributes])

  const latest = useRef({ recorded, computed, inputs, rule, bandSetId })
  latest.current = { recorded, computed, inputs, rule, bandSetId }
  const saving = useRef(false)

  const save = useDebouncedCallback(
    async (manual?: ObservationValue | null) => {
      const current = latest.current
      const currentRule = current.rule
      const nodeId = attributes.id
      if (!currentRule || !nodeId || saving.current) {
        return
      }
      const value = manual === undefined ? current.computed : manual
      // Inputs not yet read, or whose read failed, are not known to be gone.
      if (
        manual === undefined &&
        value === null &&
        (current.inputs.loading || current.inputs.failed)
      ) {
        return
      }
      const derivedFrom = current.inputs.derivedFrom
      saving.current = true
      try {
        if (deferred) {
          const record = current.recorded
          if (!record) {
            if (value === null) {
              return
            }
            const encounter =
              editor.storage.registryVariable?.contexts.encounter
            const { identifier: _identifier, ...built } =
              buildDerivedObservation({
                rule: currentRule,
                value,
                target,
                derivedFrom,
                bandSetId: current.bandSetId,
                nodeId,
                participant: undefined,
                practitioner: practitioner.reference,
                status: 'final',
                effectiveDateTime: encounter?.period?.start,
              })
            await node.create(ExtendedObservation.fromFhirResource(built))
            return
          }
          if (
            recordedDerivationKey(record.toFhirResource()) ===
            computedDerivationKey({
              rule: currentRule,
              value,
              derivedFrom,
              bandSetId: current.bandSetId,
            })
          ) {
            return
          }
          await node.patch((o) => {
            o.setCalculated(
              derivedObservationFields({
                rule: currentRule,
                value,
                target,
                derivedFrom,
                bandSetId: current.bandSetId,
                existingExtensions: o.toFhirResource().extension,
              })
            )
          }, record)
          return
        }
        if (!attributes.reference) {
          // No participant, no record: it would have no subject to be about.
          if (value === null || !participant) {
            return
          }
          const encounter = editor.storage.registryVariable?.contexts.encounter
          const created = await medplum.createResourceIfNoneExist(
            buildDerivedObservation({
              rule: currentRule,
              value,
              target,
              derivedFrom,
              bandSetId: current.bandSetId,
              nodeId,
              participant,
              practitioner: practitioner.reference,
              status: recordStatus,
              effectiveDateTime: encounter?.period?.start,
            }),
            derivedObservationQuery(nodeId, participant)
          )
          if (created.id) {
            cacheKeys.invalidateMutation(queryClient, 'Observation', created.id)
            updateAttributes({ reference: `Observation/${created.id}` })
          }
          return
        }
        const record = current.recorded
        if (
          !record ||
          recordedDerivationKey(record.toFhirResource()) ===
            computedDerivationKey({
              rule: currentRule,
              value,
              derivedFrom,
              bandSetId: current.bandSetId,
            })
        ) {
          return
        }
        await patch({
          instance: record,
          mutateFn: (o) => {
            o.setCalculated(
              derivedObservationFields({
                rule: currentRule,
                value,
                target,
                derivedFrom,
                bandSetId: current.bandSetId,
                existingExtensions: o.toFhirResource().extension,
              })
            )
          },
        })
      } catch (error) {
        toast.error(
          `Failed to save ${attributes.codingDisplay}${error instanceof Error ? `: ${error.message}` : ''}`
        )
      } finally {
        saving.current = false
      }
    },
    DERIVED_SAVE_DEBOUNCE_MS
  )

  // Recompute and save when what the rule computes differs from the record:
  // a new value, rule or input record, or no value once an input is gone.
  // A value that is null only because an input is unread (or its read
  // failed) is not saved, so it never clears the record.
  const computedKey = rule
    ? computedDerivationKey({
        rule,
        value: computed,
        derivedFrom: inputs.derivedFrom,
        bandSetId,
      })
    : ''
  const recordedKey = recorded
    ? recordedDerivationKey(recorded.toFhirResource())
    : ''
  const loading = Boolean(attributes.reference) && !recorded
  const unsettled = computed === null && (inputs.loading || inputs.failed)
  useEffect(() => {
    if (
      !editable ||
      override ||
      loading ||
      unsettled ||
      !rule ||
      !attributes.id
    ) {
      return
    }
    if (computedKey === recordedKey) {
      return
    }
    if (computed === null && !attributes.reference && !recorded) {
      return
    }
    void save()
  }, [
    editable,
    override,
    loading,
    unsettled,
    rule,
    attributes.id,
    attributes.reference,
    recorded,
    computed,
    computedKey,
    recordedKey,
    save,
  ])

  const setManualValue = useCallback(
    (value: number | undefined) => {
      const unit = computed?.kind === 'number' ? computed.unit : undefined
      updateAttributes({ derivedOverride: true })
      let manual: ObservationValue | null = null
      if (value !== undefined) {
        manual = { kind: 'number', value }
        if (unit) {
          manual.unit = unit
        }
      }
      void save(manual)
      void save.flush()
    },
    [computed, save, updateAttributes]
  )

  const recalculate = useCallback(() => {
    updateAttributes({ derivedOverride: false })
  }, [updateAttributes])

  return {
    rule,
    alternatives,
    computed,
    recorded,
    shown,
    band,
    bandSet,
    sources: inputs.sources,
    requirementValues,
    missingInputs: inputs.missing,
    absentFromNote: inputs.absentFromNote,
    missingRequirements: unmetRequirements,
    override,
    setManualValue,
    recalculate,
  }
}
